/**
 * ProgressPanel - Real-Time Processing Status Display
 *
 * Shows step-by-step progress for file processing, analysis, etc.
 * Features animated indicators and duration tracking.
 *
 * @module components/ProgressPanel
 */

import React from 'react';

import { Layers, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';

/**
 * ProgressPanel Component
 *
 * @param {Object} props
 * @param {Array} props.steps - Array of step objects with {id, label, icon, status, duration}
 * @param {boolean} props.isVisible - Whether to show the panel
 * @param {string} props.title - Panel header title
 */
const ProgressPanel = ({ steps, isVisible, title = 'Processing' }) => {
  if (!isVisible || !steps || steps.length === 0) return null;

  const completedCount = steps.filter(s => s.status === 'complete').length;
  const progress = Math.round((completedCount / steps.length) * 100);

  return (
    <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-lg p-4 mb-4 border border-slate-700 shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Layers className="h-3.5 w-3.5 text-amber-400 animate-pulse" />
            </div>
            <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full animate-ping" />
          </div>
          <span className="text-sm font-medium text-white">{title}</span>
        </div>
        <span className="text-xs text-slate-400">{progress}%</span>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-slate-700 rounded-full mb-3 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Steps list */}
      <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
        {steps.map((step, idx) => (
          <div
            key={step.id || idx}
            className={`flex items-center gap-2 text-xs transition-all duration-300 ${
              step.status === 'complete'
                ? 'text-slate-400'
                : step.status === 'running'
                  ? 'text-white'
                  : step.status === 'failed'
                    ? 'text-red-400'
                    : 'text-slate-500'
            }`}
          >
            {/* Status indicator */}
            <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
              {step.status === 'complete' ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
              ) : step.status === 'running' ? (
                <Loader2 className="h-3.5 w-3.5 text-amber-400 animate-spin" />
              ) : step.status === 'failed' ? (
                <AlertCircle className="h-3.5 w-3.5 text-red-400" />
              ) : (
                <div className="w-2 h-2 rounded-full bg-slate-600" />
              )}
            </div>

            {/* Icon */}
            <span className="w-5 text-center">{step.icon || '○'}</span>

            {/* Label */}
            <span className="flex-1">{step.label}</span>

            {/* Duration */}
            {step.duration && (
              <span className="text-slate-500">{(step.duration / 1000).toFixed(1)}s</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProgressPanel;
